<template>
  <v-input
    class="w-otp-field"
    :error-messages="errorMessages"
    :hide-details="hideDetails"
  >
    <div class="w-otp-field__content">
      <label
        v-if="computedLabel"
        class="w-otp-field__label"
      >
        {{ computedLabel }}
      </label>

      <div
        class="w-otp-field__grid"
        :style="gridStyle"
        @paste="onPaste"
      >
        <input
          v-for="(digit, index) in otpDigits"
          :key="index"
          :ref="`otp${index}`"
          v-model="otpDigits[index]"
          class="w-otp-field__box"
          type="text"
          inputmode="numeric"
          autocomplete="one-time-code"
          maxlength="1"
          @focus="onFocus"
          @blur="onBlur"
          @input="onDigitInput(index, $event)"
          @keydown="onKeyDown(index, $event)"
        >
      </div>
    </div>
  </v-input>
</template>

<script>
import { VInput } from 'vuetify/lib';
import requireable from '@/mixins/component/requireable-mixin';

/**
 * @component WorkTotpField
 * @description Input field for one-time authentication codes (TOTP/OTP).
 * Displays individual digit fields side by side and supports
 * automatic advancing and pasting.
 *
 * @prop {String} [value=''] - Current OTP code (v-model).
 * @prop {Number} [length=6] - Number of OTP digits.
 *
 * @emits {String} input - Emitted when the code changes.
 */
export default {
  name: 'WorkTotpField',
  mixins: [requireable],
  props: {
    ...VInput.options.props,
    value: { type: String, default: '' },
    length: { type: Number, default: 6 },
  },
  data() {
    return {
      otpDigits: this.createDigits(this.value),
    };
  },
  computed: {
    gridStyle() {
      return {
        gridTemplateColumns: `repeat(${this.length}, var(--w-otp-box-size))`,
      };
    },
  },
  watch: {
    value(newValue) {
      const sanitized = this.sanitize(newValue);
      if (sanitized === this.otpDigits.join('')) {
        return;
      }

      this.otpDigits = this.createDigits(sanitized);
    },
    length() {
      this.otpDigits = this.createDigits(this.otpDigits.join(''));
      this.emitValue();
    },
  },
  methods: {
    createDigits(rawValue) {
      const sanitized = this.sanitize(rawValue);
      const digits = Array.from({ length: this.length }, () => '');

      sanitized.split('').forEach((digit, index) => {
        digits[index] = digit;
      });

      return digits;
    },
    sanitize(rawValue) {
      return String(rawValue || '')
        .replace(/\D/g, '')
        .slice(0, this.length);
    },
    focus() {
      const firstEmptyIndex = this.otpDigits.findIndex(x => !x);
      const index = firstEmptyIndex === -1 ? this.length - 1 : firstEmptyIndex;
      this.focusInput(Math.max(index, 0));
    },
    focusInput(index) {
      this.$nextTick(() => {
        const ref = this.$refs[`otp${index}`];
        const input = Array.isArray(ref) ? ref[0] : ref;
        if (input && typeof input.focus === 'function') {
          input.focus();
        }
      });
    },
    emitValue() {
      this.$emit('input', this.otpDigits.join(''));
    },
    onFocus() {
      this.$emit('focus', this.otpDigits.join(''));
    },
    onBlur() {
      this.$nextTick(() => {
        if (!this.$el?.contains(document.activeElement)) {
          this.$emit('blur', this.otpDigits.join(''));
        }
      });
    },
    onDigitInput(index, event) {
      const inputValue = this.sanitize(event?.target?.value || '');
      const digit = inputValue ? inputValue[inputValue.length - 1] : '';

      this.$set(this.otpDigits, index, digit);
      this.emitValue();

      if (digit && index < this.length - 1) {
        this.focusInput(index + 1);
      }
    },
    onKeyDown(index, event) {
      if (event.key === 'Backspace' && !this.otpDigits[index] && index > 0) {
        event.preventDefault();
        this.focusInput(index - 1);
      } else if (event.key === 'ArrowLeft' && index > 0) {
        event.preventDefault();
        this.focusInput(index - 1);
      } else if (event.key === 'ArrowRight' && index < this.length - 1) {
        event.preventDefault();
        this.focusInput(index + 1);
      }
    },
    onPaste(event) {
      const pastedValue = this.sanitize(event.clipboardData?.getData('text') || '');

      if (!pastedValue) {
        return;
      }

      event.preventDefault();
      this.otpDigits = this.createDigits(pastedValue);
      this.emitValue();
      this.focusInput(Math.min(pastedValue.length, this.length - 1));
    },
  },
};
</script>

<style lang="scss" scoped>
.w-otp-field__content {
  width: max-content;
  max-width: 100%;
  margin: 0 auto;
}

.w-otp-field__label {
  display: inline-block;
  margin-bottom: 0.375rem;
  font-size: 1rem;
}

.w-otp-field__grid {
  display: grid;
  width: max-content;
  max-width: 100%;
  gap: 0.5rem;
}

.w-otp-field__box {
  width: var(--w-otp-box-size);
  height: var(--w-otp-box-size);
  text-align: center;
  font-size: 1.25rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  border: 1px solid var(--v-primary-lighten4);
  border-radius: 0.5rem;
  outline: none;
  background-color: $input-bg-color;
}

.w-otp-field__box:focus {
  border-color: var(--v-primary-base);
}

.w-otp-field {
  --w-otp-box-size: 3rem;
}
</style>